'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import { Brain, Activity, Zap, TrendingUp, TrendingDown, Minus, Trash2, RefreshCw, ChevronDown, ChevronUp, Database, AlertTriangle, Clock, BarChart2, Eye, EyeOff,  } from 'lucide-react';
import { eegSessionService, EEGSessionRecord, EEGSnapshotRecord } from '@/lib/services/eegSessionService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function trendIcon(values: (number | null)[]): React.ReactNode {
  const valid = values.filter((v): v is number => v !== null);
  if (valid.length < 2) return <Minus size={12} className="text-zinc-500" />;
  const delta = valid[valid.length - 1] - valid[0];
  if (delta > 2) return <TrendingUp size={12} className="text-emerald-400" />;
  if (delta < -2) return <TrendingDown size={12} className="text-red-400" />;
  return <Minus size={12} className="text-zinc-500" />;
}

function trendColor(values: (number | null)[], higherIsBetter = true): string {
  const valid = values.filter((v): v is number => v !== null);
  if (valid.length < 2) return '#8BAFD4';
  const delta = valid[valid.length - 1] - valid[0];
  if (Math.abs(delta) < 2) return '#8BAFD4';
  const improving = higherIsBetter ? delta > 0 : delta < 0;
  return improving ? '#34d399' : '#f87171';
}

const METRIC_COLORS: Record<string, string> = {
  avgCognitiveLoad: '#F97316',
  avgAttentionScore: '#22d3ee',
  avgThetaAlphaRatio: '#a78bfa',
  avgInterHemisphericCoherence: '#34d399',
  avgAlphaPower: '#fbbf24',
  avgBetaPower: '#60a5fa',
  avgThetaPower: '#f472b6',
  avgDeltaPower: '#94a3b8',
};

const METRIC_LABELS: Record<string, string> = {
  avgCognitiveLoad: 'Cognitive Load',
  avgAttentionScore: 'Attention',
  avgThetaAlphaRatio: 'Theta/Alpha Ratio',
  avgInterHemisphericCoherence: 'Coherence',
  avgAlphaPower: 'Alpha Power',
  avgBetaPower: 'Beta Power',
  avgThetaPower: 'Theta Power',
  avgDeltaPower: 'Delta Power',
};

// ─── Metric Badge ─────────────────────────────────────────────────────────────
function MetricBadge({ label, value, unit = '', color }: { label: string; value: number | null; unit?: string; color: string }) {
  return (
    <div className="flex flex-col items-center px-2.5 py-2 rounded-lg bg-[#0B1628]/80 border border-[#1E3A5F]/40 min-w-[68px]">
      <span className="text-[9px] text-zinc-500 mb-0.5 text-center leading-tight">{label}</span>
      <span className="text-[12px] font-mono font-bold tabular-nums" style={{ color }}>
        {value !== null ? value.toFixed(value < 10 ? 1 : 0) : '—'}
        {value !== null && <span className="text-[9px] font-normal text-zinc-500 ml-0.5">{unit}</span>}
      </span>
    </div>
  );
}

// ─── Session Row ──────────────────────────────────────────────────────────────
function SessionRow({
  session,
  index,
  isSelected,
  onSelect,
  onDelete,
}: {
  session: EEGSessionRecord;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const alzheimerBiomarker = session.biomarkers?.find((b) => b.biomarkerType === 'alzheimer');
  const adhdBiomarker = session.biomarkers?.find((b) => b.biomarkerType === 'adhd');
  const aiSummary = session.aiAnalysis?.find((a) => a.analysisType === 'session_summary');

  return (
    <div
      className="rounded-xl border transition-all duration-200"
      style={{
        borderColor: isSelected ? 'rgba(249,115,22,0.5)' : 'rgba(30,58,95,0.5)',
        background: isSelected ? 'rgba(249,115,22,0.04)' : 'rgba(11,22,40,0.6)',
        boxShadow: isSelected ? '0 0 0 1px rgba(249,115,22,0.2)' : 'none',
      }}
    >
      {/* Header row */}
      <div className="flex items-center gap-3 px-3 py-2.5">
        {/* Session index badge */}
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold flex-shrink-0"
          style={{
            background: 'linear-gradient(135deg, rgba(249,115,22,0.15), rgba(30,58,95,0.3))',
            border: '1px solid rgba(249,115,22,0.25)',
            color: '#F97316',
          }}
        >
          {index + 1}
        </div>

        {/* Date + duration */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[12px] font-semibold text-zinc-200 truncate">
              {session.label ?? session.sessionKey}
            </span>
            <span className="text-[10px] text-zinc-500 font-mono">{formatDuration(session.durationSeconds)}</span>
          </div>
          <span className="text-[10px] text-zinc-600">{formatDate(session.startedAt)}</span>
        </div>

        {/* Quick metrics */}
        <div className="hidden sm:flex items-center gap-1.5">
          <div className="flex flex-col items-center">
            <span className="text-[9px] text-zinc-600">Load</span>
            <span className="text-[11px] font-mono font-bold text-[#F97316]">
              {session.avgCognitiveLoad?.toFixed(0) ?? '—'}
            </span>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-[9px] text-zinc-600">Attn</span>
            <span className="text-[11px] font-mono font-bold text-cyan-400">
              {session.avgAttentionScore?.toFixed(0) ?? '—'}
            </span>
          </div>
          {alzheimerBiomarker && (
            <div className="flex flex-col items-center">
              <span className="text-[9px] text-zinc-600">AD Risk</span>
              <span className="text-[11px] font-mono font-bold text-purple-400">
                {alzheimerBiomarker.severity ?? '—'}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={onSelect}
            className="px-2 py-1 rounded-md text-[10px] font-medium transition-all"
            style={{
              background: isSelected ? 'rgba(249,115,22,0.15)' : 'rgba(30,58,95,0.4)',
              border: `1px solid ${isSelected ? 'rgba(249,115,22,0.4)' : 'rgba(30,58,95,0.6)'}`,
              color: isSelected ? '#F97316' : '#8BAFD4',
            }}
          >
            {isSelected ? 'Selected' : 'Compare'}
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-1 rounded-md text-zinc-500 hover:text-zinc-300 transition-colors"
          >
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          <button
            onClick={onDelete}
            className="p-1 rounded-md text-zinc-600 hover:text-red-400 transition-colors"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-3 pb-3 border-t border-[#1E3A5F]/30 pt-2.5 space-y-2.5">
          {/* Metrics row */}
          <div className="flex flex-wrap gap-1.5">
            <MetricBadge label="Cog Load" value={session.avgCognitiveLoad} unit="/100" color="#F97316" />
            <MetricBadge label="Attention" value={session.avgAttentionScore} unit="/100" color="#22d3ee" />
            <MetricBadge label="Coherence" value={session.avgInterHemisphericCoherence} unit="%" color="#34d399" />
            <MetricBadge label="θ/α Ratio" value={session.avgThetaAlphaRatio} color="#a78bfa" />
            <MetricBadge label="Alpha" value={session.avgAlphaPower} unit="µV²" color="#fbbf24" />
            <MetricBadge label="Beta" value={session.avgBetaPower} unit="µV²" color="#60a5fa" />
            <MetricBadge label="Theta" value={session.avgThetaPower} unit="µV²" color="#f472b6" />
          </div>

          {/* Biomarkers */}
          {(alzheimerBiomarker || adhdBiomarker) && (
            <div className="flex flex-wrap gap-2">
              {alzheimerBiomarker && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-purple-950/30 border border-purple-800/30">
                  <Brain size={11} className="text-purple-400" />
                  <span className="text-[10px] text-purple-300">
                    AD: <span className="font-semibold">{alzheimerBiomarker.severity ?? '—'}</span>
                    {alzheimerBiomarker.severityScore !== null && (
                      <span className="text-purple-500 ml-1">({alzheimerBiomarker.severityScore?.toFixed(0)})</span>
                    )}
                  </span>
                  {alzheimerBiomarker.dataSource === 'real_api' && (
                    <span className="text-[8px] text-emerald-400 font-mono">● REAL</span>
                  )}
                </div>
              )}
              {adhdBiomarker && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-950/30 border border-amber-800/30">
                  <Zap size={11} className="text-amber-400" />
                  <span className="text-[10px] text-amber-300">
                    ADHD: <span className="font-semibold">{adhdBiomarker.tbrClassification ?? '—'}</span>
                    {adhdBiomarker.tbrValue !== null && (
                      <span className="text-amber-500 ml-1">(TBR {adhdBiomarker.tbrValue?.toFixed(2)})</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* AI Summary */}
          {aiSummary && (
            <div className="rounded-lg bg-[#060D1A]/60 border border-[#1E3A5F]/30 p-2.5">
              <div className="flex items-center gap-1.5 mb-1">
                <Brain size={10} className="text-[#F97316]" />
                <span className="text-[9px] text-[#F97316] font-semibold uppercase tracking-wider">AI Session Summary</span>
                {aiSummary.modelUsed && (
                  <span className="text-[8px] text-zinc-600 font-mono">{aiSummary.modelUsed}</span>
                )}
              </div>
              <p className="text-[11px] text-zinc-400 leading-relaxed line-clamp-3">{aiSummary.analysisText}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Trend Chart ──────────────────────────────────────────────────────────────
function TrendChart({
  sessions,
  metric,
  label,
  color,
  unit,
}: {
  sessions: EEGSessionRecord[];
  metric: keyof EEGSessionRecord;
  label: string;
  color: string;
  unit?: string;
}) {
  const data = sessions
    .slice()
    .reverse()
    .map((s, i) => ({
      session: `S${i + 1}`,
      value: (s[metric] as number | null) ?? null,
      date: formatDate(s.startedAt),
    }))
    .filter((d) => d.value !== null);

  if (data.length < 2) return null;

  const values = data.map((d) => d.value as number);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;

  return (
    <div className="rounded-xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-semibold" style={{ color }}>{label}</span>
        <div className="flex items-center gap-1">
          {trendIcon(values)}
          <span className="text-[10px] font-mono" style={{ color: trendColor(values) }}>
            {values.length > 1 ? `${(values[values.length - 1] - values[0] > 0 ? '+' : '')}${(values[values.length - 1] - values[0]).toFixed(1)}` : ''}
            {unit}
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={80}>
        <LineChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,58,95,0.3)" />
          <XAxis dataKey="session" tick={{ fontSize: 9, fill: '#4A7AB5' }} />
          <YAxis tick={{ fontSize: 9, fill: '#4A7AB5' }} />
          <Tooltip
            contentStyle={{ background: '#0B1628', border: '1px solid rgba(30,58,95,0.8)', borderRadius: 8, fontSize: 11 }}
            labelStyle={{ color: '#8BAFD4' }}
            formatter={(v: number) => [`${v.toFixed(1)}${unit ?? ''}`, label]}
          />
          <ReferenceLine y={mean} stroke="rgba(255,255,255,0.1)" strokeDasharray="4 4" />
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={{ fill: color, r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Comparison Bar Chart ─────────────────────────────────────────────────────
function ComparisonChart({ sessions }: { sessions: EEGSessionRecord[] }) {
  const data = sessions
    .slice()
    .reverse()
    .map((s, i) => ({
      name: `S${i + 1}`,
      'Cog Load': s.avgCognitiveLoad?.toFixed(0) ?? 0,
      'Attention': s.avgAttentionScore?.toFixed(0) ?? 0,
      'Coherence': s.avgInterHemisphericCoherence?.toFixed(0) ?? 0,
    }));

  return (
    <div className="rounded-xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-3">
      <span className="text-[11px] font-semibold text-zinc-300 block mb-2">Session Comparison — Core Metrics</span>
      <ResponsiveContainer width="100%" height={140}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,58,95,0.3)" />
          <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#4A7AB5' }} />
          <YAxis tick={{ fontSize: 9, fill: '#4A7AB5' }} domain={[0, 100]} />
          <Tooltip
            contentStyle={{ background: '#0B1628', border: '1px solid rgba(30,58,95,0.8)', borderRadius: 8, fontSize: 11 }}
          />
          <Legend wrapperStyle={{ fontSize: 10, color: '#8BAFD4' }} />
          <Bar dataKey="Cog Load" fill="#F97316" radius={[3, 3, 0, 0]} />
          <Bar dataKey="Attention" fill="#22d3ee" radius={[3, 3, 0, 0]} />
          <Bar dataKey="Coherence" fill="#34d399" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Snapshot Overlay Chart ───────────────────────────────────────────────────
function SnapshotOverlayChart({
  sessionSnapshots,
  selectedSessions,
}: {
  sessionSnapshots: Record<string, EEGSnapshotRecord[]>;
  selectedSessions: EEGSessionRecord[];
}) {
  const [metric, setMetric] = useState<'cognitiveLoad' | 'attentionScore' | 'thetaAlphaRatio'>('cognitiveLoad');

  const metricOptions = [
    { key: 'cognitiveLoad', label: 'Cognitive Load', color: '#F97316' },
    { key: 'attentionScore', label: 'Attention', color: '#22d3ee' },
    { key: 'thetaAlphaRatio', label: 'θ/α Ratio', color: '#a78bfa' },
  ] as const;

  // Build overlay data: align by snapshot_time index
  const maxLen = Math.max(...selectedSessions.map((s) => sessionSnapshots[s.id]?.length ?? 0));
  if (maxLen === 0) return null;

  const data = Array.from({ length: maxLen }, (_, i) => {
    const point: Record<string, number | string> = { t: i };
    selectedSessions.forEach((s, si) => {
      const snaps = sessionSnapshots[s.id] ?? [];
      const snap = snaps[i];
      if (snap) {
        point[`S${si + 1}`] = snap[metric] ?? 0;
      }
    });
    return point;
  });

  const sessionColors = ['#F97316', '#22d3ee', '#a78bfa', '#34d399', '#fbbf24'];

  return (
    <div className="rounded-xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-3">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <span className="text-[11px] font-semibold text-zinc-300">Intra-Session Overlay</span>
        <div className="flex gap-1">
          {metricOptions.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setMetric(opt.key)}
              className="px-2 py-0.5 rounded text-[9px] font-medium transition-all"
              style={{
                background: metric === opt.key ? `${opt.color}22` : 'rgba(30,58,95,0.3)',
                border: `1px solid ${metric === opt.key ? opt.color : 'rgba(30,58,95,0.5)'}`,
                color: metric === opt.key ? opt.color : '#8BAFD4',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,58,95,0.3)" />
          <XAxis dataKey="t" tick={{ fontSize: 9, fill: '#4A7AB5' }} label={{ value: 'Snapshot #', position: 'insideBottom', fontSize: 9, fill: '#4A7AB5' }} />
          <YAxis tick={{ fontSize: 9, fill: '#4A7AB5' }} />
          <Tooltip
            contentStyle={{ background: '#0B1628', border: '1px solid rgba(30,58,95,0.8)', borderRadius: 8, fontSize: 11 }}
          />
          <Legend wrapperStyle={{ fontSize: 10, color: '#8BAFD4' }} />
          {selectedSessions.map((s, si) => (
            <Line
              key={s.id}
              type="monotone"
              dataKey={`S${si + 1}`}
              stroke={sessionColors[si % sessionColors.length]}
              strokeWidth={1.5}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SessionHistoryPanel() {
  const [sessions, setSessions] = useState<EEGSessionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sessionSnapshots, setSessionSnapshots] = useState<Record<string, EEGSnapshotRecord[]>>({});
  const [showTrends, setShowTrends] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const loadedSnapshotsRef = useRef<Set<string>>(new Set());

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await eegSessionService.fetchSessions(30);
      setSessions(data);
    } catch (err: unknown) {
      setError('Unable to load session history. Check Supabase connection.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Auto-refresh when a new session is saved (dispatched by SessionRecordingControls)
  useEffect(() => {
    const handler = () => {
      // Small delay to let Supabase write complete
      setTimeout(() => loadSessions(), 1500);
    };
    window.addEventListener('session:saved', handler);
    return () => window.removeEventListener('session:saved', handler);
  }, [loadSessions]);

  // Load snapshots for selected sessions
  useEffect(() => {
    selectedIds.forEach(async (id) => {
      if (loadedSnapshotsRef.current.has(id)) return;
      loadedSnapshotsRef.current.add(id);
      try {
        const snaps = await eegSessionService.fetchSnapshots(id);
        setSessionSnapshots((prev) => ({ ...prev, [id]: snaps }));
      } catch {
        // non-critical
      }
    });
  }, [selectedIds]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < 5) {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      await eegSessionService.deleteSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    } catch {
      // silent
    } finally {
      setDeletingId(null);
    }
  }, []);

  const selectedSessions = sessions.filter((s) => selectedIds.has(s.id));

  // Trend data for longitudinal charts
  const trendSessions = sessions.slice(0, 10);

  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div
        className="rounded-xl border border-[#1E3A5F]/70 bg-[#0B1628]/90 backdrop-blur-sm overflow-hidden"
        style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.4)' }}
      >
        <div className="h-[1px] bg-gradient-to-r from-transparent via-[#F97316]/50 to-transparent" />
        <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Database size={15} className="text-[#F97316]" />
            <span className="text-[13px] font-semibold text-zinc-200">Session History</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-[#0F1E35] border border-[#1E3A5F]/60 text-zinc-400">
              {sessions.length} sessions
            </span>
            {selectedIds.size > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-[#F97316]/10 border border-[#F97316]/30 text-[#F97316]">
                {selectedIds.size} selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTrends((v) => !v)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-[#0F1E35] border border-[#1E3A5F]/60 text-zinc-400 hover:text-zinc-200 transition-all"
            >
              {showTrends ? <EyeOff size={12} /> : <Eye size={12} />}
              {showTrends ? 'Hide Trends' : 'Show Trends'}
            </button>
            <button
              onClick={loadSessions}
              disabled={loading}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-[#0F1E35] border border-[#1E3A5F]/60 text-zinc-400 hover:text-zinc-200 transition-all"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ── Loading / Error ── */}
      {loading && (
        <div className="flex items-center justify-center py-10 gap-3">
          <RefreshCw size={16} className="animate-spin text-[#F97316]" />
          <span className="text-[13px] text-zinc-500">Loading session history…</span>
        </div>
      )}

      {error && !loading && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-950/20 border border-red-800/30">
          <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
          <span className="text-[12px] text-red-300">{error}</span>
        </div>
      )}

      {!loading && !error && sessions.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Database size={32} className="text-zinc-700" />
          <span className="text-[13px] text-zinc-500">No sessions saved yet.</span>
          <span className="text-[11px] text-zinc-600">Start a recording and stop it to persist your first session.</span>
        </div>
      )}

      {/* ── Longitudinal Trend Charts ── */}
      {!loading && showTrends && trendSessions.length >= 2 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <BarChart2 size={13} className="text-[#F97316]" />
            <span className="text-[12px] font-semibold text-zinc-300">Longitudinal Cognitive Trends</span>
            <span className="text-[10px] text-zinc-600">(last {trendSessions.length} sessions)</span>
          </div>

          {/* Comparison bar chart */}
          <ComparisonChart sessions={trendSessions} />

          {/* Individual trend sparklines */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            <TrendChart sessions={trendSessions} metric="avgCognitiveLoad" label="Cognitive Load" color="#F97316" unit="/100" />
            <TrendChart sessions={trendSessions} metric="avgAttentionScore" label="Attention Score" color="#22d3ee" unit="/100" />
            <TrendChart sessions={trendSessions} metric="avgInterHemisphericCoherence" label="Hemispheric Coherence" color="#34d399" unit="%" />
            <TrendChart sessions={trendSessions} metric="avgThetaAlphaRatio" label="Theta/Alpha Ratio" color="#a78bfa" />
            <TrendChart sessions={trendSessions} metric="avgAlphaPower" label="Alpha Power" color="#fbbf24" unit=" µV²" />
            <TrendChart sessions={trendSessions} metric="avgBetaPower" label="Beta Power" color="#60a5fa" unit=" µV²" />
          </div>
        </div>
      )}

      {/* ── Intra-session overlay (selected sessions) ── */}
      {selectedSessions.length >= 2 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Activity size={13} className="text-cyan-400" />
            <span className="text-[12px] font-semibold text-zinc-300">
              Intra-Session Comparison ({selectedSessions.length} sessions)
            </span>
          </div>
          <SnapshotOverlayChart
            sessionSnapshots={sessionSnapshots}
            selectedSessions={selectedSessions}
          />
        </div>
      )}

      {/* ── Session List ── */}
      {!loading && sessions.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Clock size={13} className="text-zinc-400" />
            <span className="text-[12px] font-semibold text-zinc-300">All Sessions</span>
            <span className="text-[10px] text-zinc-600">Select up to 5 for comparison</span>
          </div>
          {sessions.map((session, i) => (
            <SessionRow
              key={session.id}
              session={session}
              index={i}
              isSelected={selectedIds.has(session.id)}
              onSelect={() => toggleSelect(session.id)}
              onDelete={() => handleDelete(session.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
